import { Abi } from '@polkadot/api-contract';
import { TypeRegistry } from '@polkadot/types';
import { assert, u8aToString, hexToNumber, hexToU8a, u8aToHex, u8aConcatStrict } from '@polkadot/util';
import { defineMethod } from '@substrate/txwrapper-core';
import { DefaultBalanceModule, Balance, Balances } from '@talismn/balances';
import { githubTokenLogoUrl } from '@talismn/chaindata-provider';
import isEqual from 'lodash/isEqual';
import anylogger from 'anylogger';

var packageJson = {
	name: "@talismn/balances-substrate-psp22",
	version: "0.0.0-pr1304-20240208214653",
	author: "Talisman",
	homepage: "https://talisman.xyz",
	license: "UNLICENSED",
	publishConfig: {
		access: "public"
	},
	repository: {
		directory: "packages/balances-substrate-psp22",
		type: "git",
		url: "https://github.com/talismansociety/talisman.git"
	},
	main: "dist/talismn-balances-substrate-psp22.cjs.js",
	module: "dist/talismn-balances-substrate-psp22.esm.js",
	files: [
		"/dist"
	],
	engines: {
		node: ">=18"
	},
	scripts: {
		test: "jest",
		lint: "eslint src --max-warnings 0",
		clean: "rm -rf dist && rm -rf .turbo rm -rf node_modules"
	},
	dependencies: {
		"@talismn/balances": "workspace:*",
		"@talismn/chaindata-provider": "workspace:*",
		"@talismn/mutate-metadata": "workspace:*",
		"@talismn/util": "workspace:*",
		anylogger: "^1.0.11",
		lodash: "4.17.21"
	},
	devDependencies: {
		"@polkadot/api-contract": "^10.1.4",
		"@polkadot/types": "^10.1.4",
		"@polkadot/util": "^11.1.1",
		"@substrate/txwrapper-core": "*",
		"@talismn/eslint-config": "workspace:*",
		"@talismn/tsconfig": "workspace:*",
		"@types/jest": "^27.5.1",
		"@types/lodash": "^4.14.180",
		eslint: "^8.52.0",
		jest: "^28.1.0",
		"ts-jest": "^28.0.2",
		typescript: "^5.2.2"
	},
	peerDependencies: {
		"@polkadot/api-contract": "^10.x",
		"@polkadot/types": "10.x",
		"@polkadot/util": "11.x",
		"@substrate/txwrapper-core": "4.x"
	},
	eslintConfig: {
		root: true,
		"extends": [
			"@talismn/eslint-config/base"
		]
	}
};

var log = anylogger(packageJson.name);

var psp22Abi = {
	source: {
		hash: "0x9adaf0c0a3d26ea6dc77ef8cbf1a682100a7ab3f3cd0d3d6022ace606b7484a7",
		language: "ink! 4.0.1",
		compiler: "rustc 1.69.0-nightly",
		build_info: {
			build_mode: "Debug",
			cargo_contract_version: "2.2.1",
			rust_toolchain: "nightly-x86_64-unknown-linux-gnu",
			wasm_opt_settings: {
				keep_debug_symbols: false,
				optimization_passes: "Z"
			}
		}
	},
	contract: {
		name: "psp22",
		version: "1.0.0",
		authors: [
			"OpenBrush"
		]
	},
	spec: {
		constructors: [
			{
				args: [
					{
						label: "initial_supply",
						type: {
							displayName: [
								"Balance"
							],
							type: 0
						}
					},
					{
						label: "name",
						type: {
							displayName: [
								"Option"
							],
							type: 4
						}
					},
					{
						label: "symbol",
						type: {
							displayName: [
								"Option"
							],
							type: 4
						}
					},
					{
						label: "decimal",
						type: {
							displayName: [
								"u8"
							],
							type: 3
						}
					}
				],
				docs: [
				],
				label: "new",
				payable: false,
				returnType: {
					displayName: [
						"ink_primitives",
						"ConstructorResult"
					],
					type: 5
				},
				selector: "0x9bae9d5e"
			}
		],
		docs: [
		],
		events: [
		],
		lang_error: {
			displayName: [
				"ink",
				"LangError"
			],
			type: 6
		},
		messages: [
			{
				args: [
					{
						label: "owner",
						type: {
							displayName: [
								"psp22_external",
								"BalanceOfInput1"
							],
							type: 7
						}
					}
				],
				docs: [
					" Returns the account Balance for the specified `owner`.",
					"",
					" Returns `0` if the account is non-existent."
				],
				label: "PSP22::balance_of",
				mutates: false,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 9
				},
				selector: "0x6568382f"
			},
			{
				args: [
					{
						label: "spender",
						type: {
							displayName: [
								"psp22_external",
								"ApproveInput1"
							],
							type: 7
						}
					},
					{
						label: "value",
						type: {
							displayName: [
								"psp22_external",
								"ApproveInput2"
							],
							type: 0
						}
					}
				],
				docs: [
					" Allows `spender` to withdraw from the caller's account multiple times, up to",
					" the `value` amount.",
					"",
					" If this function is called again it overwrites the current allowance with `value`.",
					"",
					" An `Approval` event is emitted.",
					"",
					" # Errors",
					"",
					" Returns `ZeroSenderAddress` error if sender's address is zero.",
					"",
					" Returns `ZeroRecipientAddress` error if recipient's address is zero."
				],
				label: "PSP22::approve",
				mutates: true,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 10
				},
				selector: "0xb20f1bbd"
			},
			{
				args: [
					{
						label: "to",
						type: {
							displayName: [
								"psp22_external",
								"TransferInput1"
							],
							type: 7
						}
					},
					{
						label: "value",
						type: {
							displayName: [
								"psp22_external",
								"TransferInput2"
							],
							type: 0
						}
					},
					{
						label: "data",
						type: {
							displayName: [
								"psp22_external",
								"TransferInput3"
							],
							type: 2
						}
					}
				],
				docs: [
					" Transfers `value` amount of tokens from the caller's account to account `to`",
					" with additional `data` in unspecified format.",
					"",
					" On success a `Transfer` event is emitted.",
					"",
					" # Errors",
					"",
					" Returns `InsufficientBalance` error if there are not enough tokens on",
					" the caller's account Balance.",
					"",
					" Returns `ZeroSenderAddress` error if sender's address is zero.",
					"",
					" Returns `ZeroRecipientAddress` error if recipient's address is zero."
				],
				label: "PSP22::transfer",
				mutates: true,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 10
				},
				selector: "0xdb20f9f5"
			},
			{
				args: [
					{
						label: "from",
						type: {
							displayName: [
								"psp22_external",
								"TransferFromInput1"
							],
							type: 7
						}
					},
					{
						label: "to",
						type: {
							displayName: [
								"psp22_external",
								"TransferFromInput2"
							],
							type: 7
						}
					},
					{
						label: "value",
						type: {
							displayName: [
								"psp22_external",
								"TransferFromInput3"
							],
							type: 0
						}
					},
					{
						label: "data",
						type: {
							displayName: [
								"psp22_external",
								"TransferFromInput4"
							],
							type: 2
						}
					}
				],
				docs: [
					" Transfers `value` tokens on the behalf of `from` to the account `to`",
					" with additional `data` in unspecified format.",
					"",
					" This can be used to allow a contract to transfer tokens on ones behalf and/or",
					" to charge fees in sub-currencies, for example.",
					"",
					" On success a `Transfer` and `Approval` events are emitted.",
					"",
					" # Errors",
					"",
					" Returns `InsufficientAllowance` error if there are not enough tokens allowed",
					" for the caller to withdraw from `from`.",
					"",
					" Returns `InsufficientBalance` error if there are not enough tokens on",
					" the the account Balance of `from`.",
					"",
					" Returns `ZeroSenderAddress` error if sender's address is zero.",
					"",
					" Returns `ZeroRecipientAddress` error if recipient's address is zero."
				],
				label: "PSP22::transfer_from",
				mutates: true,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 10
				},
				selector: "0x54b3c76e"
			},
			{
				args: [
					{
						label: "spender",
						type: {
							displayName: [
								"psp22_external",
								"IncreaseAllowanceInput1"
							],
							type: 7
						}
					},
					{
						label: "delta_value",
						type: {
							displayName: [
								"psp22_external",
								"IncreaseAllowanceInput2"
							],
							type: 0
						}
					}
				],
				docs: [
					" Atomically increases the allowance granted to `spender` by the caller.",
					"",
					" An `Approval` event is emitted.",
					"",
					" # Errors",
					"",
					" Returns `ZeroSenderAddress` error if sender's address is zero.",
					"",
					" Returns `ZeroRecipientAddress` error if recipient's address is zero."
				],
				label: "PSP22::increase_allowance",
				mutates: true,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 10
				},
				selector: "0x96d6b57a"
			},
			{
				args: [
				],
				docs: [
					" Returns the total token supply."
				],
				label: "PSP22::total_supply",
				mutates: false,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 9
				},
				selector: "0x162df8c2"
			},
			{
				args: [
					{
						label: "spender",
						type: {
							displayName: [
								"psp22_external",
								"DecreaseAllowanceInput1"
							],
							type: 7
						}
					},
					{
						label: "delta_value",
						type: {
							displayName: [
								"psp22_external",
								"DecreaseAllowanceInput2"
							],
							type: 0
						}
					}
				],
				docs: [
					" Atomically decreases the allowance granted to `spender` by the caller.",
					"",
					" An `Approval` event is emitted.",
					"",
					" # Errors",
					"",
					" Returns `InsufficientAllowance` error if there are not enough tokens allowed",
					" by owner for `spender`.",
					"",
					" Returns `ZeroSenderAddress` error if sender's address is zero.",
					"",
					" Returns `ZeroRecipientAddress` error if recipient's address is zero."
				],
				label: "PSP22::decrease_allowance",
				mutates: true,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 10
				},
				selector: "0xfecb57d5"
			},
			{
				args: [
					{
						label: "owner",
						type: {
							displayName: [
								"psp22_external",
								"AllowanceInput1"
							],
							type: 7
						}
					},
					{
						label: "spender",
						type: {
							displayName: [
								"psp22_external",
								"AllowanceInput2"
							],
							type: 7
						}
					}
				],
				docs: [
					" Returns the amount which `spender` is still allowed to withdraw from `owner`.",
					"",
					" Returns `0` if no allowance has been set `0`."
				],
				label: "PSP22::allowance",
				mutates: false,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 9
				},
				selector: "0x4d47d921"
			},
			{
				args: [
				],
				docs: [
					" Returns the token symbol."
				],
				label: "PSP22Metadata::token_symbol",
				mutates: false,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 13
				},
				selector: "0x34205be5"
			},
			{
				args: [
				],
				docs: [
					" Returns the token name."
				],
				label: "PSP22Metadata::token_name",
				mutates: false,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 13
				},
				selector: "0x3d261bd4"
			},
			{
				args: [
				],
				docs: [
					" Returns the token decimals."
				],
				label: "PSP22Metadata::token_decimals",
				mutates: false,
				payable: false,
				returnType: {
					displayName: [
						"ink",
						"MessageResult"
					],
					type: 14
				},
				selector: "0x7271b782"
			}
		]
	},
	storage: {
		root: {
			layout: {
				struct: {
					fields: [
						{
							layout: {
								struct: {
									fields: [
										{
											layout: {
												leaf: {
													key: "0x00000000",
													ty: 0
												}
											},
											name: "supply"
										},
										{
											layout: {
												root: {
													layout: {
														leaf: {
															key: "0x1d458d3b",
															ty: 0
														}
													},
													root_key: "0x1d458d3b"
												}
											},
											name: "balances"
										},
										{
											layout: {
												root: {
													layout: {
														leaf: {
															key: "0x0abd72fb",
															ty: 0
														}
													},
													root_key: "0x0abd72fb"
												}
											},
											name: "allowances"
										},
										{
											layout: {
												"enum": {
													dispatchKey: "0x00000000",
													name: "Option",
													variants: {
														"0": {
															fields: [
															],
															name: "None"
														},
														"1": {
															fields: [
																{
																	layout: {
																		leaf: {
																			key: "0x00000000",
																			ty: 1
																		}
																	},
																	name: "0"
																}
															],
															name: "Some"
														}
													}
												}
											},
											name: "_reserved"
										}
									],
									name: "Data"
								}
							},
							name: "psp22"
						},
						{
							layout: {
								struct: {
									fields: [
										{
											layout: {
												"enum": {
													dispatchKey: "0x00000000",
													name: "Option",
													variants: {
														"0": {
															fields: [
															],
															name: "None"
														},
														"1": {
															fields: [
																{
																	layout: {
																		leaf: {
																			key: "0x00000000",
																			ty: 2
																		}
																	},
																	name: "0"
																}
															],
															name: "Some"
														}
													}
												}
											},
											name: "name"
										},
										{
											layout: {
												"enum": {
													dispatchKey: "0x00000000",
													name: "Option",
													variants: {
														"0": {
															fields: [
															],
															name: "None"
														},
														"1": {
															fields: [
																{
																	layout: {
																		leaf: {
																			key: "0x00000000",
																			ty: 2
																		}
																	},
																	name: "0"
																}
															],
															name: "Some"
														}
													}
												}
											},
											name: "symbol"
										},
										{
											layout: {
												leaf: {
													key: "0x00000000",
													ty: 3
												}
											},
											name: "decimals"
										},
										{
											layout: {
												"enum": {
													dispatchKey: "0x00000000",
													name: "Option",
													variants: {
														"0": {
															fields: [
															],
															name: "None"
														},
														"1": {
															fields: [
																{
																	layout: {
																		leaf: {
																			key: "0x00000000",
																			ty: 1
																		}
																	},
																	name: "0"
																}
															],
															name: "Some"
														}
													}
												}
											},
											name: "_reserved"
										}
									],
									name: "Data"
								}
							},
							name: "metadata"
						}
					],
					name: "MyToken"
				}
			},
			root_key: "0x00000000"
		}
	},
	types: [
		{
			id: 0,
			type: {
				def: {
					primitive: "u128"
				}
			}
		},
		{
			id: 1,
			type: {
				def: {
					tuple: [
					]
				}
			}
		},
		{
			id: 2,
			type: {
				def: {
					sequence: {
						type: 3
					}
				}
			}
		},
		{
			id: 3,
			type: {
				def: {
					primitive: "u8"
				}
			}
		},
		{
			id: 4,
			type: {
				def: {
					variant: {
						variants: [
							{
								index: 0,
								name: "None"
							},
							{
								fields: [
									{
										type: 2
									}
								],
								index: 1,
								name: "Some"
							}
						]
					}
				},
				params: [
					{
						name: "T",
						type: 2
					}
				],
				path: [
					"Option"
				]
			}
		},
		{
			id: 5,
			type: {
				def: {
					variant: {
						variants: [
							{
								fields: [
									{
										type: 1
									}
								],
								index: 0,
								name: "Ok"
							},
							{
								fields: [
									{
										type: 6
									}
								],
								index: 1,
								name: "Err"
							}
						]
					}
				},
				params: [
					{
						name: "T",
						type: 1
					},
					{
						name: "E",
						type: 6
					}
				],
				path: [
					"Result"
				]
			}
		},
		{
			id: 6,
			type: {
				def: {
					variant: {
						variants: [
							{
								index: 1,
								name: "CouldNotReadInput"
							}
						]
					}
				},
				path: [
					"ink_primitives",
					"LangError"
				]
			}
		},
		{
			id: 7,
			type: {
				def: {
					composite: {
						fields: [
							{
								type: 8,
								typeName: "[u8; 32]"
							}
						]
					}
				},
				path: [
					"ink_primitives",
					"types",
					"AccountId"
				]
			}
		},
		{
			id: 8,
			type: {
				def: {
					array: {
						len: 32,
						type: 3
					}
				}
			}
		},
		{
			id: 9,
			type: {
				def: {
					variant: {
						variants: [
							{
								fields: [
									{
										type: 0
									}
								],
								index: 0,
								name: "Ok"
							},
							{
								fields: [
									{
										type: 6
									}
								],
								index: 1,
								name: "Err"
							}
						]
					}
				},
				params: [
					{
						name: "T",
						type: 0
					},
					{
						name: "E",
						type: 6
					}
				],
				path: [
					"Result"
				]
			}
		},
		{
			id: 10,
			type: {
				def: {
					variant: {
						variants: [
							{
								fields: [
									{
										type: 11
									}
								],
								index: 0,
								name: "Ok"
							},
							{
								fields: [
									{
										type: 6
									}
								],
								index: 1,
								name: "Err"
							}
						]
					}
				},
				params: [
					{
						name: "T",
						type: 11
					},
					{
						name: "E",
						type: 6
					}
				],
				path: [
					"Result"
				]
			}
		},
		{
			id: 11,
			type: {
				def: {
					variant: {
						variants: [
							{
								fields: [
									{
										type: 1
									}
								],
								index: 0,
								name: "Ok"
							},
							{
								fields: [
									{
										type: 12
									}
								],
								index: 1,
								name: "Err"
							}
						]
					}
				},
				params: [
					{
						name: "T",
						type: 1
					},
					{
						name: "E",
						type: 12
					}
				],
				path: [
					"Result"
				]
			}
		},
		{
			id: 12,
			type: {
				def: {
					variant: {
						variants: [
							{
								fields: [
									{
										type: 2,
										typeName: "String"
									}
								],
								index: 0,
								name: "Custom"
							},
							{
								index: 1,
								name: "InsufficientBalance"
							},
							{
								index: 2,
								name: "InsufficientAllowance"
							},
							{
								index: 3,
								name: "ZeroRecipientAddress"
							},
							{
								index: 4,
								name: "ZeroSenderAddress"
							},
							{
								fields: [
									{
										type: 2,
										typeName: "String"
									}
								],
								index: 5,
								name: "SafeTransferCheckFailed"
							}
						]
					}
				},
				path: [
					"openbrush_contracts",
					"traits",
					"errors",
					"psp22",
					"PSP22Error"
				]
			}
		},
		{
			id: 13,
			type: {
				def: {
					variant: {
						variants: [
							{
								fields: [
									{
										type: 4
									}
								],
								index: 0,
								name: "Ok"
							},
							{
								fields: [
									{
										type: 6
									}
								],
								index: 1,
								name: "Err"
							}
						]
					}
				},
				params: [
					{
						name: "T",
						type: 4
					},
					{
						name: "E",
						type: 6
					}
				],
				path: [
					"Result"
				]
			}
		},
		{
			id: 14,
			type: {
				def: {
					variant: {
						variants: [
							{
								fields: [
									{
										type: 3
									}
								],
								index: 0,
								name: "Ok"
							},
							{
								fields: [
									{
										type: 6
									}
								],
								index: 1,
								name: "Err"
							}
						]
					}
				},
				params: [
					{
						name: "T",
						type: 3
					},
					{
						name: "E",
						type: 6
					}
				],
				path: [
					"Result"
				]
			}
		}
	],
	version: "4"
};

const subPsp22TokenId = (chainId, tokenSymbol) => `${chainId}-substrate-psp22-${tokenSymbol}`.toLowerCase().replace(/ /g, "-");
const SubPsp22Module = hydrate => {
  const {
    chainConnectors,
    chaindataProvider
  } = hydrate;
  const chainConnector = chainConnectors.substrate;
  assert(chainConnector, "This module requires a substrate chain connector");
  return {
    ...DefaultBalanceModule("substrate-psp22"),
    /**
     * This method is currently executed on [a squid](https://github.com/TalismanSociety/chaindata-squid/blob/0ee02818bf5caa7362e3f3664e55ef05ec8df078/src/steps/fetchDataForChains.ts#L286-L314).
     * In a future version of the balance libraries, we may build some kind of async scheduling system which will keep the chainmeta for each chain up to date without relying on a squid.
     */
    async fetchSubstrateChainMeta(chainId) {
      const isTestnet = (await chaindataProvider.getChain(chainId))?.isTestnet || false;
      return {
        isTestnet
      };
    },
    /**
     * This method is currently executed on [a squid](https://github.com/TalismanSociety/chaindata-squid/blob/0ee02818bf5caa7362e3f3664e55ef05ec8df078/src/steps/fetchDataForChains.ts#L331-L336).
     * In a future version of the balance libraries, we may build some kind of async scheduling system which will keep the list of tokens for each chain up to date without relying on a squid.
     */
    async fetchSubstrateChainTokens(chainId, chainMeta, moduleConfig) {
      const {
        isTestnet
      } = chainMeta;
      const registry = new TypeRegistry();
      const Psp22Abi = new Abi(psp22Abi);
      const contractCall = makeContractCaller({
        chainConnector,
        chainId,
        registry
      });
      const tokens = {};
      for (const tokenConfig of moduleConfig?.tokens || []) {
        try {
          let symbol = tokenConfig?.symbol ?? "Unknown";
          let decimals = tokenConfig?.decimals ?? 0;
          const existentialDeposit = tokenConfig?.ed ?? "0";
          const contractAddress = tokenConfig?.contractAddress ?? undefined;
          const coingeckoId = tokenConfig?.coingeckoId ?? undefined;
          if (contractAddress === undefined) continue;
          await (async () => {
            const [symbolResult, decimalsResult] = await Promise.all([contractCall(contractAddress, contractAddress, registry.createType("Vec<u8>", Psp22Abi.findMessage("PSP22Metadata::token_symbol").selector)), contractCall(contractAddress, contractAddress, registry.createType("Vec<u8>", Psp22Abi.findMessage("PSP22Metadata::token_decimals").selector))]);

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const symbolData = symbolResult.toJSON()?.result?.ok?.data;
            symbol = typeof symbolData === "string" && symbolData.startsWith("0x") ? u8aToString(registry.createType("Option<Vec<u8>>",
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            symbolResult.toJSON()?.result?.ok?.data)?.value)?.replace(/\p{C}/gu, "") : symbol;

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const decimalsData = decimalsResult.toJSON()?.result?.ok?.data;
            decimals = typeof decimalsData === "string" && decimalsData.startsWith("0x") ? hexToNumber(decimalsData) : decimals;
          })();
          const id = subPsp22TokenId(chainId, symbol);
          const token = {
            id,
            type: "substrate-psp22",
            isTestnet,
            symbol,
            decimals,
            logo: githubTokenLogoUrl(id),
            coingeckoId,
            existentialDeposit,
            contractAddress,
            chain: {
              id: chainId
            }
          };
          tokens[token.id] = token;
        } catch (error) {
          log.error(`Failed to build substrate-psp22 token ${tokenConfig.contractAddress} (${tokenConfig.symbol}) on ${chainId}`, error?.message ?? error);
          continue;
        }
      }
      return tokens;
    },
    // TODO: Don't create empty subscriptions
    async subscribeBalances(addressesByToken, callback) {
      let subscriptionActive = true;
      const subscriptionInterval = 12_000; // 12_000ms == 12 seconds
      const cache = new Map();
      const poll = async () => {
        if (!subscriptionActive) return;
        try {
          const balances = await this.fetchBalances(addressesByToken);

          // Don't call callback with balances which have not changed since the last poll.
          const updatedBalances = new Balances([...balances].filter(b => {
            if (isEqual(cache.get(b.id), b.toJSON())) return false;
            cache.set(b.id, b.toJSON());
            return true;
          }));
          callback(null, updatedBalances);
        } catch (error) {
          callback(error);
        } finally {
          setTimeout(poll, subscriptionInterval);
        }
      };
      setTimeout(poll, subscriptionInterval);
      return () => {
        subscriptionActive = false;
      };
    },
    async fetchBalances(addressesByToken) {
      assert(chainConnectors.substrate, "This module requires a substrate chain connector");
      const tokens = await chaindataProvider.tokens();
      const registry = new TypeRegistry();
      const Psp22Abi = new Abi(psp22Abi);
      const balanceRequests = Object.entries(addressesByToken).flatMap(([tokenId, addresses]) => addresses.map(address => [tokenId, address])).flatMap(async ([tokenId, address]) => {
        const token = tokens[tokenId];
        if (!token) {
          log.debug(`Token ${tokenId} not found`);
          return [];
        }
        if (token.type !== "substrate-psp22") {
          log.debug(`This module doesn't handle tokens of type ${token.type}`);
          return [];
        }
        const contractCall = makeContractCaller({
          chainConnector,
          chainId: token.chain.id,
          registry
        });
        if (token.contractAddress === undefined) {
          log.debug(`Token ${tokenId} of type substrate-psp22 doesn't have a contractAddress`);
          return [];
        }
        const result = await contractCall(address, token.contractAddress, registry.createType("Vec<u8>", Psp22Abi.findMessage("PSP22::balance_of").toU8a([
        // ACCOUNT
        address])));
        const balance = registry
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .createType("Balance", hexToU8a(result.toJSON()?.result?.ok?.data).slice(1)).toString();
        return new Balance({
          source: "substrate-psp22",
          status: "live",
          address,
          multiChainId: {
            subChainId: token.chain.id
          },
          chainId: token.chain.id,
          tokenId,
          free: balance
        });
      });

      // wait for balance fetches to complete
      const balanceResults = await Promise.allSettled(balanceRequests);

      // filter out errors
      const balances = balanceResults.map(result => {
        if (result.status === "rejected") {
          log.debug(result.reason);
          return false;
        }
        return result.value;
      }).filter(balance => balance !== false);

      // return to caller
      return new Balances(balances);
    },
    async transferToken({
      tokenId,
      from,
      to,
      amount,
      registry,
      metadataRpc,
      blockHash,
      blockNumber,
      nonce,
      specVersion,
      transactionVersion,
      tip
    }) {
      const token = await chaindataProvider.getToken(tokenId);
      assert(token, `Token ${tokenId} not found in store`);
      if (token.type !== "substrate-psp22") throw new Error(`This module doesn't handle tokens of type ${token.type}`);
      const chainId = token.chain.id;
      const chain = await chaindataProvider.getChain(chainId);
      assert(chain?.genesisHash, `Chain ${chainId} not found in store`);
      const {
        genesisHash
      } = chain;
      const Psp22Abi = new Abi(psp22Abi);
      const contractCall = makeContractCaller({
        chainConnector,
        chainId,
        registry
      });
      const data = registry.createType("Vec<u8>", Psp22Abi.findMessage("PSP22::transfer").toU8a([
      // TO
      to,
      // VALUE
      amount,
      // DATA
      undefined]));
      const dryRunResult = await contractCall(from, token.contractAddress, data);
      const pallet = "contracts";
      const method = "call";
      const args = {
        dest: token.contractAddress,
        value: 0,
        gasLimit: dryRunResult.gasRequired.toHex(),
        storageDepositLimit: dryRunResult.storageDeposit.isCharge ? dryRunResult.storageDeposit.asCharge.toHex() : null,
        data: data.toHex()
      };
      const unsigned = defineMethod({
        method: {
          pallet,
          name: method,
          args
        },
        address: from,
        blockHash,
        blockNumber,
        eraPeriod: 64,
        genesisHash,
        metadataRpc,
        nonce,
        specVersion,
        tip: tip ? Number(tip) : 0,
        transactionVersion
      }, {
        metadataRpc,
        registry
      });
      return {
        type: "substrate",
        tx: unsigned
      };
    }
  };
};
const makeContractCaller = ({
  chainConnector,
  chainId,
  registry
}) => async (callFrom, contractAddress, inputData) => registry.createType("ContractExecResult", await chainConnector.send(chainId, "state_call", ["ContractsApi_call", u8aToHex(u8aConcatStrict([
// origin
registry.createType("AccountId", callFrom).toU8a(),
// dest
registry.createType("AccountId", contractAddress).toU8a(),
// value
registry.createType("Balance", 0).toU8a(),
// gasLimit
registry.createType("Option<WeightV2>").toU8a(),
// storageDepositLimit
registry.createType("Option<Balance>").toU8a(),
// inputData
inputData.toU8a()]))]));

export { SubPsp22Module };
